import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { joinCompleteAppOnboardingJoinCompletePost } from '@/client/sdk.gen'
import type { UserTerritory } from '@/hooks/use-onboarding'
import { useOnboardingStepViewed } from '@/hooks/use-analytics'
import { useOnboarding } from '@/hooks/use-onboarding'
import { useAuth } from '@/hooks/use-auth'
import { StepLayout } from './-components/step-layout'
import { TerritoryMap } from './-components/territory-map'
import { useRegionToggle } from './-components/use-region-toggle'
import { RegionBadgeList } from './-components/region-badge-list'
import { Input } from '@/components/ui/input'
import { getInitials } from '@/lib/utils'

export const Route = createFileRoute('/onboarding/claim-territory')({
  component: ClaimTerritoryStep,
})

function ClaimTerritoryStep() {
  useOnboardingStepViewed('claim_territory')
  const navigate = useNavigate()
  const { companyTerritory, userTerritories, memberProfile, onboardingMode, inviteToken, firstName, lastName, password, updateState } = useOnboarding()
  const { markSessionEstablished } = useAuth()
  const isMember = onboardingMode === 'member'
  const [territoryName, setTerritoryName] = useState(userTerritories[0]?.name ?? '')
  const { regions, toggleRegion } = useRegionToggle(userTerritories[0]?.regions ?? [])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | undefined>()

  const handleNext = async () => {
    const territory: UserTerritory = {
      name: territoryName || 'My Territory',
      regions,
      claimedBy: 'You',
      color: 'hsl(237, 78%, 66%)',
    }

    if (isMember) {
      // Member mode: call join-complete, then go to report
      setIsSubmitting(true)
      setSubmitError(undefined)
      try {
        const { error: respError } = await joinCompleteAppOnboardingJoinCompletePost({
          body: {
            token: inviteToken ?? '',
            password,
            firstName,
            lastName,
            userTerritories: [{ name: territory.name, regions: territory.regions }],
          },
        })
        if (respError) {
          const detail = (respError as { detail?: string }).detail
          throw new Error(detail || 'Something went wrong')
        }
        // The backend set the auth cookie; sync AuthProvider state and the
        // session flag before entering the authenticated TAM route.
        await markSessionEstablished()
        // Member flow: claim-territory is step 2; completion bumps to 3.
        updateState({ userTerritories: [territory], currentStep: 3 })
        sessionStorage.removeItem('onboarding-state')
        globalThis.location.href = '/tam-report?from=onboarding'
      }
      catch (error: unknown) {
        setSubmitError(error instanceof Error ? error.message : 'Something went wrong')
      }
      finally {
        setIsSubmitting(false)
      }
    }
    else {
      updateState({ userTerritories: [territory], currentStep: 5 })
      navigate({ to: '/onboarding/team' })
    }
  }

  const handleBack = () => {
    if (isMember) {
      navigate({ to: '/onboarding/email' })
    }
    else {
      navigate({ to: '/onboarding/company-territory' })
    }
  }

  // Build the "You" entry for the sidebar
  const youName = memberProfile?.name ?? 'You'

  return (
    <StepLayout
      title="Claim your territory"
      subtitle="Select the regions you're responsible for"
      onNext={handleNext}
      onBack={handleBack}
      nextLabel="Confirm my territory"
      nextDisabled={regions.length === 0 || isSubmitting}
      maxWidth="xl"
    >
      <div className="space-y-4">
        {submitError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {submitError}
          </div>
        )}

        <Input
          value={territoryName}
          onChange={e => setTerritoryName(e.target.value)}
          placeholder="Name your territory (e.g. Midwest)"
        />

        <div className="flex gap-4">
          {/* Map */}
          <div className="flex-1">
            <TerritoryMap
              selectedRegions={regions}
              onToggleRegion={toggleRegion}
              backgroundRegions={companyTerritory}
              claimedTerritories={[]}
            />
          </div>

          {/* Team territories sidebar */}
          <div className="w-56 shrink-0 space-y-3">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Team territories
            </p>

            {/* Current user */}
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
              <div className="mb-2 flex items-center gap-2">
                {memberProfile?.photoUrl
                  ? (
                      <img
                        src={memberProfile.photoUrl}
                        alt={youName}
                        className="h-6 w-6 rounded-full object-cover"
                      />
                    )
                  : (
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                        {getInitials(youName)}
                      </div>
                    )}
                <span className="text-sm font-medium">{youName}</span>
                <span className="text-[10px] text-muted-foreground">(you)</span>
              </div>
              {regions.length === 0
                ? (
                    <p className="text-xs italic text-muted-foreground">
                      Click regions on the map
                    </p>
                  )
                : (
                    <RegionBadgeList regions={regions} onRemove={toggleRegion} />
                  )}
            </div>
          </div>
        </div>
      </div>
    </StepLayout>
  )
}
